import React, { useEffect, useRef, useState } from "react";
import { Music, VolumeX } from "lucide-react";

const TRACK_URL =
  "https://customer-assets.emergentagent.com/job_ceremonial-bliss-2/artifacts/bq83wb9h_Edd_Sheeran_-_Perfect_%28mp3.pm%29.mp3";

const MusicPlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  // userMuted=true only when the user explicitly paused. While false we still
  // treat the music as "on" (the icon shows Music). Browser auto-block before
  // first interaction is hidden from the UI.
  const [userMuted, setUserMuted] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.4;
    a.loop = true;

    const tryPlay = () => {
      a.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    };

    // Attempt autoplay immediately
    tryPlay();

    // Fallback: start on the first user interaction (browsers commonly block silent autoplay)
    const onGesture = () => {
      if (!userMuted && a.paused) tryPlay();
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("scroll", onGesture);
    };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    window.addEventListener("touchstart", onGesture);
    window.addEventListener("scroll", onGesture);

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);

    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("touchstart", onGesture);
      window.removeEventListener("scroll", onGesture);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, [userMuted]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      setUserMuted(false);
      a.play().catch(() => {});
    } else {
      setUserMuted(true);
      a.pause();
    }
  };

  // Show Music icon by default (only show VolumeX after explicit user mute)
  const showMuted = userMuted && !playing;

  return (
    <>
      <audio ref={audioRef} src={TRACK_URL} preload="auto" />
      <button
        type="button"
        onClick={toggle}
        data-testid="music-toggle"
        aria-label={showMuted ? "Reproducir música" : "Silenciar música"}
        className="fixed bottom-6 left-6 z-50 w-11 h-11 md:w-12 md:h-12 rounded-full bg-cream/85 backdrop-blur-md border border-olive/25 text-wine shadow-[0_8px_24px_-8px_rgba(107,45,49,0.4)] flex items-center justify-center transition-all duration-500 hover:scale-110 hover:bg-cream"
      >
        {showMuted ? (
          <VolumeX className="w-4 h-4 md:w-[18px] md:h-[18px]" strokeWidth={1.6} />
        ) : (
          <Music className="w-4 h-4 md:w-[18px] md:h-[18px]" strokeWidth={1.6} />
        )}
        {playing && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-gold animate-ping-slow" />
        )}
      </button>
    </>
  );
};

export default MusicPlayer;
